import { useState, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Grid3X3, Square, Circle, Trash2, Download, RotateCcw, RotateCw, ZoomIn, ZoomOut, LayoutGrid, Save, FolderOpen, Loader2, FileText, Move, UtensilsCrossed, X, ArrowLeft, HelpCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRID_SIZE = 20; // pixels per grid unit (1m) for display
const SNAP_SIZE = 2;  // pixels for snap-to spacing (0.1m)
const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;

// Default sizes matching Room Capacity Calculator (in metres, converted to pixels)
const elementTypes = [
  { value: "table-round", label: "Round Table", shape: "circle", size: 1.83 * GRID_SIZE, color: "#06b6d4", hasChairs: true }, // 1.83m diameter
  { value: "table-rect", label: "Rectangle Table", shape: "rect", width: 1.83 * GRID_SIZE, height: 0.76 * GRID_SIZE, color: "#06b6d4", hasChairs: true }, // 1.83m x 0.76m
  { value: "stage", label: "Stage", shape: "rect", width: 200, height: 80, color: "#eab308", configurable: true },
  { value: "dancefloor", label: "Dancefloor", shape: "rect", width: 100, height: 100, color: "#ec4899", configurable: true },
  { value: "chair", label: "Chair", shape: "circle", size: 0.5 * GRID_SIZE, color: "#a1a1aa" }, // 0.5m
  { value: "podium", label: "Podium", shape: "rect", width: 1 * GRID_SIZE, height: 0.6 * GRID_SIZE, color: "#a855f7" }, // 1m x 0.6m
  { value: "bar", label: "Bar/Buffet", shape: "rect", width: 3 * GRID_SIZE, height: 0.8 * GRID_SIZE, color: "#22c55e" }, // 3m x 0.8m
  { value: "entrance", label: "Entrance", shape: "rect", width: 1.5 * GRID_SIZE, height: 0.5 * GRID_SIZE, color: "#ef4444" }, // 1.5m x 0.5m
  { value: "text", label: "Text Label", shape: "text", width: 3 * GRID_SIZE, height: 0.8 * GRID_SIZE, color: "#f4f4f5", configurable: true },
];

export const FloorPlan = () => {
  const canvasRef = useRef(null);
  const { getAuthHeaders } = useAuth();
  const [elements, setElements] = useState([]);
  const [selectedType, setSelectedType] = useState("table-round");
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null); // Track selected group
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPositions, setDragStartPositions] = useState([]); // For group dragging
  const [roomWidth, setRoomWidth] = useState("20");
  const [roomLength, setRoomLength] = useState("15");
  const [showGrid, setShowGrid] = useState(true);
  const [nextGroupId, setNextGroupId] = useState(1); // Group ID counter
  
  // Configurable options
  const [stageWidth, setStageWidth] = useState("10");
  const [stageHeight, setStageHeight] = useState("4");
  const [dancefloorWidth, setDancefloorWidth] = useState("5");
  const [dancefloorHeight, setDancefloorHeight] = useState("5");
  const [tableChairs, setTableChairs] = useState("10");
  const [rectTableChairs, setRectTableChairs] = useState("2");

  // Text label state
  const [textContent, setTextContent] = useState("Label");
  const [textFontSize, setTextFontSize] = useState("18");
  const [textBoxWidth, setTextBoxWidth] = useState("3");   // metres
  const [textBoxHeight, setTextBoxHeight] = useState("1"); // metres

  // Metadata for placed groups so users can edit rows/cols after placement.
  // Shape: { [groupId]: { type, rows, cols, spacingX, spacingY, centerX, centerY, chairs? } }
  const [groupMeta, setGroupMeta] = useState({});
  
  // Zoom controls
  const [zoom, setZoom] = useState(1);
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 2;
  const ZOOM_STEP = 0.25;
  
  // Array placement
  const [arrayRows, setArrayRows] = useState("2");
  const [arrayCols, setArrayCols] = useState("3");

  // Save/Load state
  const [savedPlans, setSavedPlans] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentPlanName, setCurrentPlanName] = useState("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [savePlanName, setSavePlanName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Chair Planner hand-off (?tables=N&chairs=M)
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pendingChairPlan, setPendingChairPlan] = useState(() => {
    const tables = parseInt(searchParams.get("tables"));
    const chairs = parseInt(searchParams.get("chairs"));
    if (tables > 0 && chairs > 0) return { tables, chairs };
    return null;
  });

  // Room Capacity hand-off (?room_w=20&room_l=15&layout=banquet&guests=150&stage=1)
  const [pendingRoomLayout, setPendingRoomLayout] = useState(() => {
    const roomW = parseFloat(searchParams.get("room_w"));
    const roomL = parseFloat(searchParams.get("room_l"));
    const layout = searchParams.get("layout");
    const guests = parseInt(searchParams.get("guests"));
    const stage = searchParams.get("stage") === "1";
    if (roomW > 0 && roomL > 0 && layout && guests > 0) {
      return { roomW, roomL, layout, guests, stage };
    }
    return null;
  });

  // Track if user arrived from Room Capacity so the Back button stays available
  // even after they've already clicked Build Plan (dismissed the banner).
  const [cameFromRoomCapacity, setCameFromRoomCapacity] = useState(() => {
    return Boolean(searchParams.get("layout") && searchParams.get("room_w"));
  });

  const dismissChairPlan = () => {
    setPendingChairPlan(null);
    setSearchParams({}, { replace: true });
  };

  const dismissRoomLayout = () => {
    setPendingRoomLayout(null);
    setSearchParams({}, { replace: true });
  };

  // Permanently hide the persistent Back button (used by the user explicitly)
  const hidePersistentBack = () => setCameFromRoomCapacity(false);

  // Get config for selected type
  const selectedTypeConfig = elementTypes.find(t => t.value === selectedType);

  // Helper function to convert screen coordinates to canvas coordinates with center-based zoom
  const screenToCanvas = (clientX, clientY, rect) => {
    // Get position relative to canvas
    const screenX = clientX - rect.left;
    const screenY = clientY - rect.top;
    
    // Reverse the center-based zoom transformation
    // The transform is: translate(center) -> scale(zoom) -> translate(-center)
    // So to reverse: translate(center) -> scale(1/zoom) -> translate(-center)
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2;
    
    const canvasX = (screenX - centerX) / zoom + centerX;
    const canvasY = (screenY - centerY) / zoom + centerY;
    
    return { x: canvasX, y: canvasY };
  };

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    
    // Clear canvas
    ctx.fillStyle = "#18181b";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Apply zoom transform from center
    ctx.save();
    // Translate to center, scale, then translate back
    ctx.translate(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-CANVAS_WIDTH / 2, -CANVAS_HEIGHT / 2);
    
    const scaledWidth = CANVAS_WIDTH;
    const scaledHeight = CANVAS_HEIGHT;
    
    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = "#27272a";
      ctx.lineWidth = 1 / zoom;
      
      for (let x = 0; x <= scaledWidth; x += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, scaledHeight);
        ctx.stroke();
      }
      
      for (let y = 0; y <= scaledHeight; y += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(scaledWidth, y);
        ctx.stroke();
      }
    }
    
    // Draw room dimensions text
    ctx.fillStyle = "#a1a1aa";
    ctx.font = `${12 / zoom}px JetBrains Mono`;
    ctx.fillText(`Room: ${roomWidth}m × ${roomLength}m | Zoom: ${Math.round(zoom * 100)}%`, 10, 20);
    
    // Draw room outline (hollow rectangle)
    const roomW = parseFloat(roomWidth) * GRID_SIZE;
    const roomL = parseFloat(roomLength) * GRID_SIZE;
    const roomX = (scaledWidth - roomW) / 2;
    const roomY = (scaledHeight - roomL) / 2;
    
    ctx.strokeStyle = "#525252";
    ctx.lineWidth = 2 / zoom;
    ctx.setLineDash([10 / zoom, 5 / zoom]);
    ctx.strokeRect(roomX, roomY, roomW, roomL);
    ctx.setLineDash([]);
    
    // Draw elements
    elements.forEach((el, index) => {
      const typeConfig = elementTypes.find(t => t.value === el.type);
      if (!typeConfig) return;
      
      ctx.save();
      ctx.translate(el.x, el.y);
      ctx.rotate((el.rotation || 0) * Math.PI / 180);
      
      // Check if element is selected (individually or as part of group)
      const isSelected = selectedElement === index || (selectedGroup && el.groupId === selectedGroup);
      
      ctx.fillStyle = typeConfig.color;
      ctx.strokeStyle = isSelected ? "#ffffff" : typeConfig.color;
      ctx.lineWidth = isSelected ? 3 / zoom : 1 / zoom;
      
      // Get dimensions (use element-specific or default)
      const width = el.width || typeConfig.width;
      const height = el.height || typeConfig.height;
      const size = el.size || typeConfig.size;
      
      if (typeConfig.shape === "text") {
        // Text label element — dashed box outline + centred text, rotates with element.
        const w = width;
        const h = height;
        const fontSize = el.fontSize || 18;
        ctx.strokeStyle = isSelected ? "#ffffff" : "#71717a";
        ctx.lineWidth = (isSelected ? 2 : 1) / zoom;
        ctx.setLineDash([4 / zoom, 4 / zoom]);
        ctx.strokeRect(-w / 2, -h / 2, w, h);
        ctx.setLineDash([]);
        ctx.fillStyle = typeConfig.color;
        ctx.font = `600 ${fontSize}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(el.text || "Label", 0, 0, w - 8 / zoom);
      } else if (typeConfig.shape === "circle") {
        if (el.type === "table-round") {
          const spaceRadius = (3.33 / 2) * GRID_SIZE; // 3.33m diameter
          ctx.strokeStyle = isSelected ? "#ffffff" : "#4a5568";
          ctx.setLineDash([5 / zoom, 5 / zoom]);
          ctx.lineWidth = 1 / zoom;
          ctx.beginPath();
          ctx.arc(0, 0, spaceRadius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]); // Reset dash
        }
        
        // Draw table
        ctx.fillStyle = typeConfig.color;
        ctx.strokeStyle = isSelected ? "#ffffff" : typeConfig.color;
        ctx.lineWidth = isSelected ? 3 / zoom : 1 / zoom;
        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        
        // Draw chairs around round table (positioned to fit within 3.33m circle)
        if (el.type === "table-round" && el.chairs) {
          const chairCount = el.chairs;
          const chairSize = 8;
          const tableRadius = size / 2;
          const chairDistance = tableRadius + chairSize / 2 + 3; // Tight to table
          
          ctx.fillStyle = "#a1a1aa";
          
          if (chairCount <= 8) {
            // Proportional layout - distribute chairs as if it were a table of 10
            // So 8 chairs = 80% around the circle, 6 chairs = 60%, etc.
            const basePositions = 10; // Reference table size
            const proportion = chairCount / basePositions; // e.g., 8/10 = 0.8
            const arcSpan = Math.PI * 2 * proportion; // Proportional arc
            const startAngle = -arcSpan / 2; // Center arc on right (0°), gap on left
            
            for (let i = 0; i < chairCount; i++) {
              // Distribute evenly within the proportional arc
              const angle = startAngle + (arcSpan / chairCount) * i + (arcSpan / chairCount) / 2;
              const chairX = Math.cos(angle) * chairDistance;
              const chairY = Math.sin(angle) * chairDistance;
              
              ctx.beginPath();
              ctx.arc(chairX, chairY, chairSize / 2, 0, Math.PI * 2);
              ctx.fill();
            }
          } else {
            // Banquet format - chairs all around (9 or more)
            for (let i = 0; i < chairCount; i++) {
              const angle = (2 * Math.PI / chairCount) * i - Math.PI / 2;
              const chairX = Math.cos(angle) * chairDistance;
              const chairY = Math.sin(angle) * chairDistance;
              
              ctx.beginPath();
              ctx.arc(chairX, chairY, chairSize / 2, 0, Math.PI * 2);
              ctx.fill();
            }
          }
          
          // Draw just the chair count on round tables
          ctx.fillStyle = "#ffffff";
          ctx.font = `bold ${11}px JetBrains Mono`;
          ctx.textAlign = "center";
          ctx.fillText(el.chairs.toString(), 0, 4);
        }
      } else {
        ctx.fillRect(-width / 2, -height / 2, width, height);
        ctx.strokeRect(-width / 2, -height / 2, width, height);
        
        // Draw attached chairs on rectangle tables (2 or 4 chairs on long sides)
        if (el.type === "table-rect" && el.chairs) {
          const chairCount = Math.max(0, Math.min(4, parseInt(el.chairs) || 0));
          const chairSize = 8;
          const chairGap = 3; // px gap from table edge
          ctx.fillStyle = "#a1a1aa";
          // Long sides are the horizontal (width) sides — chairs attach to top and bottom
          const topY = -height / 2 - chairSize / 2 - chairGap;
          const botY = height / 2 + chairSize / 2 + chairGap;
          if (chairCount === 1) {
            // Single chair on top side, centred
            ctx.beginPath();
            ctx.arc(0, topY, chairSize / 2, 0, Math.PI * 2);
            ctx.fill();
          } else if (chairCount === 2) {
            // Two chairs — both on the top (stage-facing) side, evenly spaced
            const xs = [-width / 4, width / 4];
            xs.forEach((cx) => {
              ctx.beginPath();
              ctx.arc(cx, topY, chairSize / 2, 0, Math.PI * 2);
              ctx.fill();
            });
          } else if (chairCount === 3) {
            // Two on top, one on bottom centre
            const topXs = [-width / 4, width / 4];
            topXs.forEach((cx) => {
              ctx.beginPath();
              ctx.arc(cx, topY, chairSize / 2, 0, Math.PI * 2);
              ctx.fill();
            });
            ctx.beginPath();
            ctx.arc(0, botY, chairSize / 2, 0, Math.PI * 2);
            ctx.fill();
          } else if (chairCount === 4) {
            // Two on each long side
            const xs = [-width / 4, width / 4];
            xs.forEach((cx) => {
              ctx.beginPath();
              ctx.arc(cx, topY, chairSize / 2, 0, Math.PI * 2);
              ctx.fill();
              ctx.beginPath();
              ctx.arc(cx, botY, chairSize / 2, 0, Math.PI * 2);
              ctx.fill();
            });
          }
        }

        // Only draw label for entrance (sized to fit)
        if (el.type === "entrance") {
          ctx.fillStyle = "#ffffff";
          ctx.font = `bold ${8}px Inter`;
          ctx.textAlign = "center";
          ctx.fillText("Entry", 0, 3);
        }
        // Only draw label for stage
        if (el.type === "stage") {
          ctx.fillStyle = "#ffffff";
          ctx.font = `bold ${12}px Inter`;
          ctx.textAlign = "center";
          ctx.fillText("Stage", 0, 4);
        }
        // Only draw label for dancefloor
        if (el.type === "dancefloor") {
          ctx.fillStyle = "#ffffff";
          ctx.font = `bold ${12}px Inter`;
          ctx.textAlign = "center";
          ctx.fillText("Dancefloor", 0, 4);
        }
        // Only draw label for bar/buffet
        if (el.type === "bar") {
          ctx.fillStyle = "#ffffff";
          ctx.font = `bold ${10}px Inter`;
          ctx.textAlign = "center";
          ctx.fillText("Bar/Buffet", 0, 4);
        }
      }
      
      // Draw rotation indicator for selected element
      if (isSelected && el.rotation) {
        ctx.fillStyle = "#eab308";
        ctx.font = `${9}px JetBrains Mono`;
        ctx.textAlign = "center";
        const yOffset = typeConfig.shape === "circle" ? -size/2 - 15 : -height/2 - 10;
        ctx.fillText(`${el.rotation}°`, 0, yOffset);
      }
      
      ctx.restore();
    });
    
    // Restore zoom transform
    ctx.restore();
  }, [elements, selectedElement, selectedGroup, showGrid, roomWidth, roomLength, zoom]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // Account for center-based zoom when calculating coordinates
    const coords = screenToCanvas(e.clientX, e.clientY, rect);
    const rawX = coords.x;
    const rawY = coords.y;
    
    // Check if clicking on existing element
    const clickedIndex = elements.findIndex((el) => {
      const typeConfig = elementTypes.find(t => t.value === el.type);
      if (!typeConfig) return false;
      
      const width = el.width || typeConfig.width || typeConfig.size;
      const height = el.height || typeConfig.height || typeConfig.size;
      
      if (typeConfig.shape === "circle") {
        const dist = Math.sqrt((el.x - rawX) ** 2 + (el.y - rawY) ** 2);
        return dist < (el.size || typeConfig.size) / 2 + 20; // Add margin for chairs
      } else {
        return (
          rawX >= el.x - width / 2 &&
          rawX <= el.x + width / 2 &&
          rawY >= el.y - height / 2 &&
          rawY <= el.y + height / 2
        );
      }
    });
    
    if (clickedIndex >= 0) {
      const clickedElement = elements[clickedIndex];
      setSelectedElement(clickedIndex);
      // If element belongs to a group, select the whole group
      if (clickedElement.groupId) {
        setSelectedGroup(clickedElement.groupId);
      } else {
        setSelectedGroup(null);
      }
    } else {
      // Clicking on empty space deselects current selection
      setSelectedElement(null);
      setSelectedGroup(null);
    }
  };

  const handleMouseDown = (e) => {
    if (selectedElement === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const coords = screenToCanvas(e.clientX, e.clientY, rect);
    const x = coords.x;
    const y = coords.y;
    
    const el = elements[selectedElement];
    setDragOffset({ x: x - el.x, y: y - el.y });
    
    // Store starting positions for all group elements
    if (selectedGroup) {
      const groupPositions = elements
        .map((element, idx) => ({ idx, x: element.x, y: element.y, groupId: element.groupId }))
        .filter(item => item.groupId === selectedGroup);
      setDragStartPositions(groupPositions);
    }
    
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || selectedElement === null) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const coords = screenToCanvas(e.clientX, e.clientY, rect);
    const rawX = coords.x;
    const rawY = coords.y;
    // Snap to 0.1m increments
    const newX = Math.round((rawX - dragOffset.x) / SNAP_SIZE) * SNAP_SIZE;
    const newY = Math.round((rawY - dragOffset.y) / SNAP_SIZE) * SNAP_SIZE;
    
    // Calculate delta from the original position of the clicked element
    const clickedEl = elements[selectedElement];
    const deltaX = newX - clickedEl.x;
    const deltaY = newY - clickedEl.y;
    
    if (selectedGroup && dragStartPositions.length > 0) {
      // Move all elements in the group
      const originalClickedPos = dragStartPositions.find(p => p.idx === selectedElement);
      if (originalClickedPos) {
        const groupDeltaX = newX - originalClickedPos.x;
        const groupDeltaY = newY - originalClickedPos.y;
        
        setElements(prev => prev.map((el, i) => {
          if (el.groupId === selectedGroup) {
            const originalPos = dragStartPositions.find(p => p.idx === i);
            if (originalPos) {
              return {
                ...el,
                x: Math.round((originalPos.x + groupDeltaX) / SNAP_SIZE) * SNAP_SIZE,
                y: Math.round((originalPos.y + groupDeltaY) / SNAP_SIZE) * SNAP_SIZE
              };
            }
          }
          return el;
        }));
      }
    } else {
      // Move single element
      setElements(prev => prev.map((el, i) => 
        i === selectedElement ? { ...el, x: newX, y: newY } : el
      ));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStartPositions([]);
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e) => {
    if (selectedElement === null) return;
    e.preventDefault(); // Prevent scrolling while dragging
    
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const coords = screenToCanvas(touch.clientX, touch.clientY, rect);
    const x = coords.x;
    const y = coords.y;
    
    const el = elements[selectedElement];
    setDragOffset({ x: x - el.x, y: y - el.y });
    
    // Store starting positions for all group elements
    if (selectedGroup) {
      const groupPositions = elements
        .map((element, idx) => ({ idx, x: element.x, y: element.y, groupId: element.groupId }))
        .filter(item => item.groupId === selectedGroup);
      setDragStartPositions(groupPositions);
    }
    
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || selectedElement === null) return;
    e.preventDefault(); // Prevent scrolling while dragging
    
    const touch = e.touches[0];
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const coords = screenToCanvas(touch.clientX, touch.clientY, rect);
    const rawX = coords.x;
    const rawY = coords.y;
    // Snap to 0.1m increments
    const newX = Math.round((rawX - dragOffset.x) / SNAP_SIZE) * SNAP_SIZE;
    const newY = Math.round((rawY - dragOffset.y) / SNAP_SIZE) * SNAP_SIZE;
    
    if (selectedGroup && dragStartPositions.length > 0) {
      // Move all elements in the group
      const originalClickedPos = dragStartPositions.find(p => p.idx === selectedElement);
      if (originalClickedPos) {
        const groupDeltaX = newX - originalClickedPos.x;
        const groupDeltaY = newY - originalClickedPos.y;
        
        setElements(prev => prev.map((el, i) => {
          if (el.groupId === selectedGroup) {
            const originalPos = dragStartPositions.find(p => p.idx === i);
            if (originalPos) {
              return {
                ...el,
                x: Math.round((originalPos.x + groupDeltaX) / SNAP_SIZE) * SNAP_SIZE,
                y: Math.round((originalPos.y + groupDeltaY) / SNAP_SIZE) * SNAP_SIZE
              };
            }
          }
          return el;
        }));
      }
    } else {
      // Move single element
      setElements(prev => prev.map((el, i) => 
        i === selectedElement ? { ...el, x: newX, y: newY } : el
      ));
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setDragStartPositions([]);
  };

  const rotateSelected = (direction) => {
    if (selectedElement === null) return;
    
    setElements(prev => prev.map((el, i) => {
      // Rotate all elements in group, or just the selected element
      if (selectedGroup && el.groupId === selectedGroup) {
        const currentRotation = el.rotation || 0;
        const newRotation = (currentRotation + (direction * 15) + 360) % 360;
        return { ...el, rotation: newRotation };
      } else if (!selectedGroup && i === selectedElement) {
        const currentRotation = el.rotation || 0;
        const newRotation = (currentRotation + (direction * 15) + 360) % 360;
        return { ...el, rotation: newRotation };
      }
      return el;
    }));
    toast.success(`Rotated ${direction > 0 ? 'clockwise' : 'counter-clockwise'}`);
  };

  const deleteSelected = () => {
    if (selectedElement === null) return;
    
    if (selectedGroup) {
      // Delete all elements in the group
      const count = elements.filter(el => el.groupId === selectedGroup).length;
      setElements(prev => prev.filter(el => el.groupId !== selectedGroup));
      setSelectedGroup(null);
      toast.success(`Deleted ${count} elements`);
    } else {
      setElements(prev => prev.filter((_, i) => i !== selectedElement));
      toast.success("Element deleted");
    }
    setSelectedElement(null);
  };

  const clearAll = () => {
    setElements([]);
    setSelectedElement(null);
    setSelectedGroup(null);
    setCurrentPlanId(null);
    setCurrentPlanName("");
    toast.success("Canvas cleared");
  };

  const downloadPlan = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = "floor-plan.png";
    link.href = canvas.toDataURL();
    link.click();
    toast.success("Floor plan downloaded");
  };

  // Save/Load functions
  const fetchSavedPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/floorplans`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSavedPlans(data);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const handleSave = async () => {
    if (!savePlanName.trim()) {
      toast.error("Please enter a plan name");
      return;
    }

    setIsSaving(true);
    try {
      const planData = {
        name: savePlanName.trim(),
        elements: elements,
        room_width: parseFloat(roomWidth),
        room_length: parseFloat(roomLength)
      };

      let response;
      if (currentPlanId) {
        // Update existing plan
        response = await fetch(`${API_URL}/api/user/floorplans/${currentPlanId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders()
          },
          body: JSON.stringify(planData)
        });
      } else {
        // Create new plan
        response = await fetch(`${API_URL}/api/user/floorplans`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders()
          },
          body: JSON.stringify(planData)
        });
      }

      if (response.ok) {
        const savedPlan = await response.json();
        setCurrentPlanId(savedPlan.id);
        setCurrentPlanName(savedPlan.name);
        setSaveDialogOpen(false);
        setSavePlanName("");
        toast.success(`Floor plan "${savedPlan.name}" saved`);
        fetchSavedPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save plan");
      }
    } catch (error) {
      toast.error("Error saving plan");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoad = async (plan) => {
    setIsLoading(true);
    try {
      setElements(plan.elements || []);
      setRoomWidth(plan.room_width?.toString() || "20");
      setRoomLength(plan.room_length?.toString() || "15");
      setCurrentPlanId(plan.id);
      setCurrentPlanName(plan.name);
      setSelectedElement(null);
      setSelectedGroup(null);
      setLoadDialogOpen(false);
      toast.success(`Loaded "${plan.name}"`);
    } catch (error) {
      toast.error("Error loading plan");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (planId, planName) => {
    try {
      const response = await fetch(`${API_URL}/api/user/floorplans/${planId}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success(`Deleted "${planName}"`);
        if (currentPlanId === planId) {
          setCurrentPlanId(null);
          setCurrentPlanName("");
        }
        fetchSavedPlans();
      } else {
        toast.error("Failed to delete plan");
      }
    } catch (error) {
      toast.error("Error deleting plan");
    }
  };

  const openSaveDialog = () => {
    setSavePlanName(currentPlanName || "");
    setSaveDialogOpen(true);
  };

  const openLoadDialog = () => {
    fetchSavedPlans();
    setLoadDialogOpen(true);
  };

  // Zoom functions
  const zoomIn = () => {
    setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const zoomOut = () => {
    setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  };

  const resetZoom = () => {
    setZoom(1);
  };

  // Preset label library — one-click adds a text element with sensible defaults.
  const PRESET_LABELS = [
    "Bar",
    "AV Desk",
    "Registration",
    "Coats",
    "VIP Entrance",
    "Green Room",
    "Fire Exit",
  ];

  const addLabelPreset = (label) => {
    // Auto-spread: each new preset drops offset from the last so they don't stack.
    // Staircase pattern — 4 columns, then wraps to next row.
    const existingLabels = elements.filter((e) => e.type === "text").length;
    const step = 0.7 * GRID_SIZE; // 0.7m diagonal step
    const cols = 4;
    const offsetX = ((existingLabels % cols) - (cols - 1) / 2) * step;
    const offsetY = Math.floor(existingLabels / cols) * step;

    const centerX = Math.round((CANVAS_WIDTH / 2 + offsetX) / SNAP_SIZE) * SNAP_SIZE;
    const centerY = Math.round((CANVAS_HEIGHT / 2 + offsetY) / SNAP_SIZE) * SNAP_SIZE;
    const newElement = {
      type: "text",
      x: centerX,
      y: centerY,
      rotation: 0,
      text: label,
      fontSize: 20,
      width: 3 * GRID_SIZE,
      height: 1 * GRID_SIZE,
    };
    setElements(prev => [...prev, newElement]);
    setSelectedElement(elements.length);
    setSelectedGroup(null);
    toast.success(`Added "${label}" label`);
  };

  // Add single element function (adds to center of canvas)
  const addElement = () => {
    const typeConfig = elementTypes.find(t => t.value === selectedType);
    if (!typeConfig) return;

    // Calculate center position
    const centerX = Math.round((CANVAS_WIDTH / 2) / SNAP_SIZE) * SNAP_SIZE;
    const centerY = Math.round((CANVAS_HEIGHT / 2) / SNAP_SIZE) * SNAP_SIZE;

    const newElement = { type: selectedType, x: centerX, y: centerY, rotation: 0 };

    // Add configurable properties
    if (selectedType === "stage") {
      newElement.width = parseFloat(stageWidth) * GRID_SIZE;
      newElement.height = parseFloat(stageHeight) * GRID_SIZE;
    }

    if (selectedType === "dancefloor") {
      newElement.width = parseFloat(dancefloorWidth) * GRID_SIZE;
      newElement.height = parseFloat(dancefloorHeight) * GRID_SIZE;
    }

    if (selectedType === "table-round") {
      newElement.chairs = parseInt(tableChairs);
    }

    if (selectedType === "table-rect") {
      newElement.chairs = parseInt(rectTableChairs);
    }

    if (selectedType === "text") {
      newElement.text = textContent || "Label";
      newElement.fontSize = parseInt(textFontSize) || 18;
      newElement.width = parseFloat(textBoxWidth) * GRID_SIZE;
      newElement.height = parseFloat(textBoxHeight) * GRID_SIZE;
    }

    setElements([...elements, newElement]);
    setSelectedElement(elements.length); // Select the new element
    setSelectedGroup(null);
    toast.success("Element added");
  };

  // Group placement function
  const placeGroup = () => {
    const rows = parseInt(arrayRows) || 1;
    const cols = parseInt(arrayCols) || 1;
    
    // Element-specific spacing defaults (in metres)
    let spacingX, spacingY;
    if (selectedType === "table-round") {
      spacingX = 3.33 * GRID_SIZE; // 3.33m for round tables
      spacingY = 3.33 * GRID_SIZE;
    } else if (selectedType === "chair") {
      spacingX = 1.1 * GRID_SIZE;  // 1.1m x 0.9m for chairs
      spacingY = 0.9 * GRID_SIZE;
    } else {
      // Default spacing for other elements (use element width/height + 1m buffer)
      const typeConfig = elementTypes.find(t => t.value === selectedType);
      const baseWidth = typeConfig?.width || typeConfig?.size || GRID_SIZE * 2;
      const baseHeight = typeConfig?.height || typeConfig?.size || GRID_SIZE * 2;
      spacingX = baseWidth + GRID_SIZE;
      spacingY = baseHeight + GRID_SIZE;
    }
    
    // Calculate starting position (center the array on canvas)
    const totalWidth = (cols - 1) * spacingX;
    const totalHeight = (rows - 1) * spacingY;
    const startX = (CANVAS_WIDTH - totalWidth) / 2;
    const startY = (CANVAS_HEIGHT - totalHeight) / 2;
    
    const newElements = [];
    const groupId = nextGroupId; // Assign same group ID to all array elements
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        // Snap to 0.1m increments
        const x = Math.round((startX + col * spacingX) / SNAP_SIZE) * SNAP_SIZE;
        const y = Math.round((startY + row * spacingY) / SNAP_SIZE) * SNAP_SIZE;
        
        const newElement = { type: selectedType, x, y, rotation: 0, groupId };
        
        // Add configurable properties
        if (selectedType === "stage") {
          newElement.width = parseFloat(stageWidth) * GRID_SIZE;
          newElement.height = parseFloat(stageHeight) * GRID_SIZE;
        }
        
        if (selectedType === "dancefloor") {
          newElement.width = parseFloat(dancefloorWidth) * GRID_SIZE;
          newElement.height = parseFloat(dancefloorHeight) * GRID_SIZE;
        }
        
        if (selectedType === "table-round") {
          newElement.chairs = parseInt(tableChairs);
        }

        if (selectedType === "table-rect") {
          newElement.chairs = parseInt(rectTableChairs);
        }
        
        newElements.push(newElement);
      }
    }
    
    setElements([...elements, ...newElements]);
    // Track group metadata for later editing
    setGroupMeta(prev => ({
      ...prev,
      [groupId]: {
        type: selectedType,
        rows,
        cols,
        spacingX,
        spacingY,
        centerX: CANVAS_WIDTH / 2,
        centerY: CANVAS_HEIGHT / 2,
        chairs:
          selectedType === "table-round"
            ? parseInt(tableChairs)
            : selectedType === "table-rect"
            ? parseInt(rectTableChairs)
            : undefined,
        stageWidth: selectedType === "stage" ? parseFloat(stageWidth) : undefined,
        stageHeight: selectedType === "stage" ? parseFloat(stageHeight) : undefined,
        dancefloorWidth:
          selectedType === "dancefloor" ? parseFloat(dancefloorWidth) : undefined,
        dancefloorHeight:
          selectedType === "dancefloor" ? parseFloat(dancefloorHeight) : undefined,
      },
    }));
    setNextGroupId(prev => prev + 1); // Increment for next group
    setSelectedElement(null);
    setSelectedGroup(groupId); // Auto-select the new group
    toast.success(`Added ${rows * cols} elements as group`);
  };

  // Regenerate an existing group with new row/col counts (keeps its centre).
  const regenerateGroup = (groupId, newRows, newCols) => {
    const meta = groupMeta[groupId];
    if (!meta) {
      toast.error("Group metadata not found — cannot resize");
      return;
    }
    const rows = Math.max(1, Math.min(50, parseInt(newRows) || 1));
    const cols = Math.max(1, Math.min(50, parseInt(newCols) || 1));
    const { type, spacingX, spacingY, centerX, centerY, chairs, stageWidth: sW, stageHeight: sH, dancefloorWidth: dW, dancefloorHeight: dH } = meta;

    const totalWidth = (cols - 1) * spacingX;
    const totalHeight = (rows - 1) * spacingY;
    const startX = centerX - totalWidth / 2;
    const startY = centerY - totalHeight / 2;

    const newGroupElements = [];
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const x = Math.round((startX + c * spacingX) / SNAP_SIZE) * SNAP_SIZE;
        const y = Math.round((startY + r * spacingY) / SNAP_SIZE) * SNAP_SIZE;
        const el = { type, x, y, rotation: 0, groupId };
        if (type === "table-round" || type === "table-rect") el.chairs = chairs;
        if (type === "stage" && sW && sH) {
          el.width = sW * GRID_SIZE;
          el.height = sH * GRID_SIZE;
        }
        if (type === "dancefloor" && dW && dH) {
          el.width = dW * GRID_SIZE;
          el.height = dH * GRID_SIZE;
        }
        newGroupElements.push(el);
      }
    }

    // Remove old group elements, add new ones
    setElements(prev => [...prev.filter(el => el.groupId !== groupId), ...newGroupElements]);
    setGroupMeta(prev => ({
      ...prev,
      [groupId]: { ...meta, rows, cols },
    }));
    toast.success(`Group resized to ${rows} × ${cols}`);
  };

  // Auto-place round tables from Chair Planner hand-off
  const autoPlaceChairPlan = () => {
    if (!pendingChairPlan) return;
    const { tables: targetCount, chairs: chairsPerTable } = pendingChairPlan;

    const roomW = parseFloat(roomWidth) || 20;
    const roomL = parseFloat(roomLength) || 15;
    const tableSpace = 3.33; // metres of space per round table

    // Fit as many columns as room width allows
    const maxColsByRoom = Math.max(1, Math.floor(roomW / tableSpace));
    // Pick cols based on room aspect ratio to keep grid balanced
    const idealCols = Math.ceil(Math.sqrt(targetCount * (roomW / Math.max(roomL, 1))));
    const cols = Math.max(1, Math.min(maxColsByRoom, idealCols));
    const rows = Math.ceil(targetCount / cols);

    const spacingX = tableSpace * GRID_SIZE;
    const spacingY = tableSpace * GRID_SIZE;
    const totalWidth = (cols - 1) * spacingX;
    const totalHeight = (rows - 1) * spacingY;
    const startX = (CANVAS_WIDTH - totalWidth) / 2;
    const startY = (CANVAS_HEIGHT - totalHeight) / 2;

    const newElements = [];
    const groupId = nextGroupId;
    let count = 0;

    for (let row = 0; row < rows && count < targetCount; row++) {
      for (let col = 0; col < cols && count < targetCount; col++) {
        const x = Math.round((startX + col * spacingX) / SNAP_SIZE) * SNAP_SIZE;
        const y = Math.round((startY + row * spacingY) / SNAP_SIZE) * SNAP_SIZE;
        newElements.push({
          type: "table-round",
          x,
          y,
          rotation: 0,
          groupId,
          chairs: chairsPerTable,
        });
        count += 1;
      }
    }

    setElements(prev => [...prev, ...newElements]);
    setNextGroupId(prev => prev + 1);
    setSelectedElement(null);
    setSelectedGroup(groupId);
    setTableChairs(String(chairsPerTable));
    toast.success(`Placed ${count} round tables (${chairsPerTable} chairs each)`);
    dismissChairPlan();
  };

  // ----- Auto-place a full room layout from Room Capacity tool -----
  const autoPlaceRoomLayout = () => {
    if (!pendingRoomLayout) return;
    const { roomW, roomL, layout, guests, stage } = pendingRoomLayout;

    // 1) Set the room dimensions (in metres)
    setRoomWidth(String(roomW));
    setRoomLength(String(roomL));

    // 2) Helper: snap a coord to SNAP_SIZE grid
    const snap = (v) => Math.round(v / SNAP_SIZE) * SNAP_SIZE;

    // 3) Fresh canvas — clear existing elements
    const newElements = [];
    // Distinct groups per element type so users can move stage / dancefloor /
    // back-tables individually while keeping the main layout as one editable group.
    let idCursor = nextGroupId;
    const mainGroupId = idCursor++; // primary audience layout
    const backTablesGroupId = idCursor++; // 2 back rect tables
    const newGroupMeta = {};

    // Room bounds in pixels (canvas centred)
    const roomWpx = roomW * GRID_SIZE;
    const roomLpx = roomL * GRID_SIZE;
    const roomLeft = (CANVAS_WIDTH - roomWpx) / 2;
    const roomTop = (CANVAS_HEIGHT - roomLpx) / 2;

    // 4) Place stage at top-centre of room (if requested) — UNGROUPED so it can be moved individually.
    let stageBottomPx = roomTop + 0.5 * GRID_SIZE; // default just inside the room
    if (stage) {
      const stageW = Math.max(3, Math.min(8, roomW * 0.5));
      const stageD = Math.max(2, Math.min(4, roomL * 0.15));
      const stageWpx = stageW * GRID_SIZE;
      const stageDpx = stageD * GRID_SIZE;
      const stageX = snap(roomLeft + (roomWpx - stageWpx) / 2 + stageWpx / 2);
      const stageY = snap(roomTop + 0.8 * GRID_SIZE + stageDpx / 2);
      newElements.push({
        type: "stage",
        x: stageX,
        y: stageY,
        rotation: 0,
        // no groupId — individually selectable
        width: stageWpx,
        height: stageDpx,
      });
      stageBottomPx = stageY + stageDpx / 2;
    }

    // 5) Audience zone: leave 3m at the back for the 2 rect tables opposite the stage.
    const backTablesReserveM = 3;
    const audienceTop = stageBottomPx + 1.5 * GRID_SIZE;
    const audienceBottom = roomTop + roomLpx - backTablesReserveM * GRID_SIZE;
    const audienceWpx = roomWpx - 2 * GRID_SIZE; // 1m margin each side
    const audienceLpx = Math.max(1, audienceBottom - audienceTop);

    let placedLabel = "";

    if (layout === "theatre") {
      // Theatre: rows of chairs facing stage with smart aisle handling.
      const chairPitchX = 0.55 * GRID_SIZE;
      const rowPitchY = 0.85 * GRID_SIZE;
      const aisleW = 1.2 * GRID_SIZE;
      const blockChairs = 10;

      const maxRowWidth = audienceWpx;
      let chairsPerRow = 0;
      for (let n = 1; n <= 200; n += 1) {
        const blocks = Math.ceil(n / blockChairs);
        const totalW = n * chairPitchX + (blocks - 1) * (aisleW - chairPitchX);
        if (totalW > maxRowWidth) break;
        chairsPerRow = n;
      }
      if (chairsPerRow === 0) chairsPerRow = 1;

      const rowsAvailable = Math.max(1, Math.floor(audienceLpx / rowPitchY));
      const target = Math.min(guests, chairsPerRow * rowsAvailable);
      const rowsNeeded = Math.ceil(target / chairsPerRow);

      const numBlocks = Math.ceil(chairsPerRow / blockChairs);
      const blockSizes = [];
      const baseSize = Math.floor(chairsPerRow / numBlocks);
      const extras = chairsPerRow % numBlocks;
      for (let b = 0; b < numBlocks; b += 1) {
        blockSizes.push(baseSize + (b < extras ? 1 : 0));
      }

      const chairXOffsets = [];
      let cursor = 0;
      for (let b = 0; b < numBlocks; b += 1) {
        for (let k = 0; k < blockSizes[b]; k += 1) {
          chairXOffsets.push(cursor + chairPitchX / 2);
          cursor += chairPitchX;
        }
        cursor += aisleW - chairPitchX;
      }

      const fullRowWidth = chairXOffsets[chairXOffsets.length - 1] + chairPitchX / 2;
      const rowStartX = roomLeft + (roomWpx - fullRowWidth) / 2;
      const totalChairsBlockH = rowsNeeded * rowPitchY;
      const startY = audienceTop + Math.max(0, (audienceLpx - totalChairsBlockH) / 2);

      let count = 0;
      for (let r = 0; r < rowsNeeded && count < target; r += 1) {
        const remaining = target - count;
        const chairsThisRow = Math.min(chairsPerRow, remaining);
        const rowOffsetX = chairsThisRow < chairsPerRow
          ? (fullRowWidth - (chairXOffsets[chairsThisRow - 1] + chairPitchX / 2)) / 2
          : 0;
        for (let c = 0; c < chairsThisRow && count < target; c += 1) {
          const x = rowStartX + rowOffsetX + chairXOffsets[c];
          const y = startY + r * rowPitchY + rowPitchY / 2;
          newElements.push({
            type: "chair",
            x: snap(x),
            y: snap(y),
            rotation: 0,
            groupId: mainGroupId,
          });
          count += 1;
        }
      }
      placedLabel = `${count} chairs in theatre layout`;
    } else if (layout === "classroom") {
      const tableW = 1.83 * GRID_SIZE;
      const tableD = 0.76 * GRID_SIZE;
      const tablePitchX = (1.83 + 0.4) * GRID_SIZE;
      const rowPitchY = 1.6 * GRID_SIZE;
      const chairsPerTable = 3;

      const colsAvailable = Math.max(1, Math.floor(audienceWpx / tablePitchX));
      const rowsAvailable = Math.max(1, Math.floor(audienceLpx / rowPitchY));
      const tablesNeeded = Math.ceil(guests / chairsPerTable);
      const cols = Math.min(colsAvailable, Math.max(1, tablesNeeded));
      const rowsNeeded = Math.min(rowsAvailable, Math.ceil(tablesNeeded / cols));
      const target = Math.min(tablesNeeded, cols * rowsNeeded);

      const blockW = cols * tablePitchX - (tablePitchX - tableW);
      const startX = roomLeft + (roomWpx - blockW) / 2;
      const blockH = rowsNeeded * rowPitchY;
      const startY = audienceTop + Math.max(0, (audienceLpx - blockH) / 2);

      let placed = 0;
      for (let r = 0; r < rowsNeeded && placed < target; r += 1) {
        const remaining = target - placed;
        const colsThisRow = Math.min(cols, remaining);
        const rowOffsetX = colsThisRow < cols
          ? ((cols - colsThisRow) * tablePitchX) / 2
          : 0;
        for (let c = 0; c < colsThisRow && placed < target; c += 1) {
          const tx = startX + rowOffsetX + c * tablePitchX + tableW / 2;
          const ty = startY + r * rowPitchY + tableD / 2;
          newElements.push({
            type: "table-rect",
            x: snap(tx),
            y: snap(ty),
            rotation: 0,
            groupId: mainGroupId,
            width: tableW,
            height: tableD,
          });
          for (let k = 0; k < chairsPerTable; k += 1) {
            const chairX = startX + rowOffsetX + c * tablePitchX + (k + 0.5) * (tableW / chairsPerTable);
            const chairY = startY + r * rowPitchY + tableD + 0.35 * GRID_SIZE;
            newElements.push({
              type: "chair",
              x: snap(chairX),
              y: snap(chairY),
              rotation: 0,
              groupId: mainGroupId,
            });
          }
          placed += 1;
        }
      }
      placedLabel = `${placed} classroom tables (${placed * chairsPerTable} seats)`;
    } else if (layout === "banquet" || layout === "cabaret" || layout === "banquet-dancefloor") {
      const chairsPerTable = layout === "cabaret" ? 8 : 10;
      const totalTables = Math.ceil(guests / chairsPerTable);
      const tableSpace = 3.33 * GRID_SIZE;

      // For banquet+dancefloor, reserve a 5m × 5m dancefloor in front of audience
      // — placed as an UNGROUPED element so it can be moved/rotated individually.
      let floorAvailableTop = audienceTop;
      if (layout === "banquet-dancefloor") {
        const dfSize = 5 * GRID_SIZE;
        const dfX = snap(roomLeft + roomWpx / 2);
        const dfY = snap(audienceTop + dfSize / 2);
        newElements.push({
          type: "dancefloor",
          x: dfX,
          y: dfY,
          rotation: 0,
          // no groupId — individually selectable
          width: dfSize,
          height: dfSize,
        });
        floorAvailableTop = audienceTop + dfSize + 1 * GRID_SIZE;
      }
      const tablesAreaLpx = audienceBottom - floorAvailableTop;

      const colsByRoom = Math.max(1, Math.floor(audienceWpx / tableSpace));
      const rowsByRoom = Math.max(1, Math.floor(tablesAreaLpx / tableSpace));
      const ideal = Math.ceil(Math.sqrt(totalTables * (audienceWpx / Math.max(tablesAreaLpx, 1))));
      const cols = Math.max(1, Math.min(colsByRoom, ideal, totalTables));
      const rowsNeeded = Math.max(1, Math.min(rowsByRoom, Math.ceil(totalTables / cols)));
      const placedTotal = Math.min(totalTables, cols * rowsNeeded);

      const blockW = (cols - 1) * tableSpace;
      const blockH = (rowsNeeded - 1) * tableSpace;
      const startX = roomLeft + (roomWpx - blockW) / 2;
      const startY = floorAvailableTop + Math.max(0, (tablesAreaLpx - blockH) / 2);

      // Cabaret tables face the stage — chairs on bottom half (arc rotated 90° clockwise).
      const tableRotation = layout === "cabaret" ? 90 : 0;

      let placed = 0;
      for (let r = 0; r < rowsNeeded && placed < placedTotal; r += 1) {
        const remaining = placedTotal - placed;
        const colsThisRow = Math.min(cols, remaining);
        const rowOffsetX = colsThisRow < cols
          ? ((cols - colsThisRow) * tableSpace) / 2
          : 0;
        for (let c = 0; c < colsThisRow && placed < placedTotal; c += 1) {
          const x = snap(startX + rowOffsetX + c * tableSpace);
          const y = snap(startY + r * tableSpace);
          newElements.push({
            type: "table-round",
            x,
            y,
            rotation: tableRotation,
            groupId: mainGroupId,
            chairs: chairsPerTable,
          });
          placed += 1;
        }
      }
      placedLabel = `${placed} round tables (${chairsPerTable} chairs each)${layout === "banquet-dancefloor" ? " + dancefloor" : ""}`;

      // Record group meta for cabaret/banquet so users can edit rows/cols post-placement
      newGroupMeta[mainGroupId] = {
        type: "table-round",
        rows: rowsNeeded,
        cols,
        spacingX: tableSpace,
        spacingY: tableSpace,
        centerX: startX + blockW / 2,
        centerY: startY + blockH / 2,
        chairs: chairsPerTable,
      };
    } else {
      placedLabel = "Layout not recognised — placed stage only";
    }

    // 5b) TWO rectangle tables with 2 chairs each — grouped together off to one
    //     side of the room, at the back, chairs facing the stage.
    const backTableW = 1.83 * GRID_SIZE;
    const backTableD = 0.76 * GRID_SIZE;
    const backGap = 0.2 * GRID_SIZE; // 0.2m gap between the two tables
    const backLeftMargin = 1.2 * GRID_SIZE; // 1.2m from the left wall
    const backY = snap(roomTop + roomLpx - 1.2 * GRID_SIZE - backTableD / 2);
    // Both tables aligned to the LEFT side of the room, side-by-side.
    const t1X = snap(roomLeft + backLeftMargin + backTableW / 2);
    const t2X = snap(t1X + backTableW + backGap);
    [t1X, t2X].forEach((tx) => {
      newElements.push({
        type: "table-rect",
        x: tx,
        y: backY,
        rotation: 0,
        groupId: backTablesGroupId,
        width: backTableW,
        height: backTableD,
        chairs: 2, // 2 chairs each, drawn on the top (stage-facing) side
      });
    });
    newGroupMeta[backTablesGroupId] = {
      type: "table-rect",
      rows: 1,
      cols: 2,
      spacingX: backTableW + backGap,
      spacingY: backTableD + GRID_SIZE,
      centerX: (t1X + t2X) / 2,
      centerY: backY,
      chairs: 2,
    };

    // 6) Replace canvas with the new layout
    setElements(newElements);
    setGroupMeta(prev => ({ ...prev, ...newGroupMeta }));
    setNextGroupId(idCursor);
    setSelectedElement(null);
    setSelectedGroup(mainGroupId);
    toast.success(`Built ${layout} for ${guests} guests — ${placedLabel} + 2 back tables`);
    dismissRoomLayout();
  };
  return (
    <Layout 
      title={<>Floor Plan Tool <span className="text-cyan-400 text-lg md:text-xl align-middle ml-2 tracking-wider">Beta</span></>}
      subtitle="2D layout tool to plan table and stage placement — the Floor Plan Tool is currently in development."
    >
      <div className="space-y-3">
        {/* Persistent Back to Room Capacity — shown whenever user arrived from there */}
        {cameFromRoomCapacity && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-2 rounded-sm bg-secondary/50 border border-border"
            data-testid="persistent-back-strip"
          >
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="label-tactical">From Room Capacity</span>
              <span>·</span>
              <span>Try a different layout without re-entering your room details.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/planning/room-capacity")}
                data-testid="persistent-back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={hidePersistentBack}
                data-testid="persistent-back-dismiss"
                title="Hide this bar"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Chair Planner Hand-off Banner */}
        {pendingChairPlan && (
          <Card
            className="tactical-card border-cyan-500/50 bg-cyan-500/5"
            data-testid="chair-plan-banner"
          >
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 bg-cyan-500/20 rounded-sm">
                    <UtensilsCrossed className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-cyan-400">From Catering Chair Planner</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ready to place <span className="font-mono text-foreground">{pendingChairPlan.tables}</span> round tables with{" "}
                      <span className="font-mono text-foreground">{pendingChairPlan.chairs}</span> chairs each. Adjust the room size first if needed.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-center">
                  <Button
                    size="sm"
                    onClick={autoPlaceChairPlan}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="auto-place-tables-button"
                  >
                    <LayoutGrid className="h-4 w-4 mr-2" />
                    Place Tables
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={dismissChairPlan}
                    data-testid="dismiss-chair-plan-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Room Capacity Hand-off Banner */}
        {pendingRoomLayout && (
          <Card
            className="tactical-card border-cyan-500/50 bg-cyan-500/5"
            data-testid="room-layout-banner"
          >
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 bg-cyan-500/20 rounded-sm">
                    <LayoutGrid className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-cyan-400">From Room Capacity Calculator</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Build a{" "}
                      <span className="font-mono text-foreground capitalize">
                        {pendingRoomLayout.layout.replace(/-/g, " + ")}
                      </span>{" "}
                      layout for{" "}
                      <span className="font-mono text-foreground">{pendingRoomLayout.guests}</span>{" "}
                      guests in a{" "}
                      <span className="font-mono text-foreground">
                        {pendingRoomLayout.roomW} × {pendingRoomLayout.roomL} m
                      </span>{" "}
                      room
                      {pendingRoomLayout.stage ? ", with stage" : ""}.
                      <br />
                      <span className="text-xs">This will replace any existing layout on the canvas.</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-center">
                  <Button
                    size="sm"
                    onClick={autoPlaceRoomLayout}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="auto-place-layout-button"
                  >
                    <LayoutGrid className="h-4 w-4 mr-2" />
                    Build Plan
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={dismissRoomLayout}
                    data-testid="dismiss-room-layout-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Controls */}
        <Card className="tactical-card">
          <CardContent className="p-2 sm:p-3 space-y-2">
            {/* Row 1: Room size */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="label-tactical whitespace-nowrap">Room (m)</Label>
                <Input
                  type="number"
                  value={roomWidth}
                  onChange={(e) => setRoomWidth(e.target.value)}
                  className="tactical-input w-16"
                  data-testid="room-width-input"
                />
                <span className="text-muted-foreground">×</span>
                <Input
                  type="number"
                  value={roomLength}
                  onChange={(e) => setRoomLength(e.target.value)}
                  className="tactical-input w-16"
                  data-testid="room-length-input"
                />
              </div>
            </div>

            {/* Row 2: Add Element */}
            <div className="flex flex-wrap items-center gap-3 p-2 bg-zinc-900/50 rounded-md border border-border/30">
              <div className="flex items-center gap-2">
                <Label className="label-tactical">Add Element</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="tactical-input w-40" data-testid="element-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {elementTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          {type.shape === "circle" ? (
                            <Circle className="h-4 w-4" style={{ color: type.color }} />
                          ) : (
                            <Square className="h-4 w-4" style={{ color: type.color }} />
                          )}
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Stage size inputs */}
              {selectedType === "stage" && (
                <div className="flex items-center gap-2">
                  <Label className="label-tactical whitespace-nowrap">Stage (m)</Label>
                  <Input
                    type="number"
                    value={stageWidth}
                    onChange={(e) => setStageWidth(e.target.value)}
                    className="tactical-input w-16"
                    placeholder="W"
                    data-testid="stage-width-input"
                  />
                  <span className="text-muted-foreground">×</span>
                  <Input
                    type="number"
                    value={stageHeight}
                    onChange={(e) => setStageHeight(e.target.value)}
                    className="tactical-input w-16"
                    placeholder="H"
                    data-testid="stage-height-input"
                  />
                </div>
              )}

              {/* Dancefloor size inputs */}
              {selectedType === "dancefloor" && (
                <div className="flex items-center gap-2">
                  <Label className="label-tactical whitespace-nowrap">Size (m)</Label>
                  <Input
                    type="number"
                    value={dancefloorWidth}
                    onChange={(e) => setDancefloorWidth(e.target.value)}
                    className="tactical-input w-16"
                    placeholder="W"
                    data-testid="dancefloor-width-input"
                  />
                  <span className="text-muted-foreground">×</span>
                  <Input
                    type="number"
                    value={dancefloorHeight}
                    onChange={(e) => setDancefloorHeight(e.target.value)}
                    className="tactical-input w-16"
                    placeholder="H"
                    data-testid="dancefloor-height-input"
                  />
                </div>
              )}

              {/* Round table chair count */}
              {selectedType === "table-round" && (
                <div className="flex items-center gap-2">
                  <Label className="label-tactical whitespace-nowrap">Chairs</Label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    value={tableChairs}
                    onChange={(e) => setTableChairs(e.target.value)}
                    className="tactical-input w-16"
                    data-testid="table-chairs-input"
                  />
                  <span className="text-xs text-muted-foreground">
                    {parseInt(tableChairs) < 10 ? "(Cabaret)" : "(Banquet)"}
                  </span>
                </div>
              )}

              {/* Rectangle table chair count (0–4) */}
              {selectedType === "table-rect" && (
                <div className="flex items-center gap-2">
                  <Label className="label-tactical whitespace-nowrap">Chairs</Label>
                  <Input
                    type="number"
                    min="0"
                    max="4"
                    value={rectTableChairs}
                    onChange={(e) => setRectTableChairs(e.target.value)}
                    className="tactical-input w-16"
                    data-testid="rect-table-chairs-input"
                  />
                  <span className="text-xs text-muted-foreground">
                    0–4 (long sides)
                  </span>
                </div>
              )}

              {/* Text label inputs */}
              {selectedType === "text" && (
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="label-tactical whitespace-nowrap">Text</Label>
                  <Input
                    type="text"
                    value={textContent}
                    onChange={(e) => setTextContent(e.target.value)}
                    className="tactical-input w-40"
                    placeholder="e.g. Bar, VIP, Entrance"
                    data-testid="text-content-input"
                  />
                  <Label className="label-tactical whitespace-nowrap">Font</Label>
                  <Input
                    type="number"
                    min="8"
                    max="72"
                    value={textFontSize}
                    onChange={(e) => setTextFontSize(e.target.value)}
                    className="tactical-input w-16"
                    data-testid="text-fontsize-input"
                  />
                  <Label className="label-tactical whitespace-nowrap">W (m)</Label>
                  <Input
                    type="number"
                    min="0.5"
                    max="20"
                    step="0.1"
                    value={textBoxWidth}
                    onChange={(e) => setTextBoxWidth(e.target.value)}
                    className="tactical-input w-16"
                    data-testid="text-width-input"
                  />
                  <Label className="label-tactical whitespace-nowrap">H (m)</Label>
                  <Input
                    type="number"
                    min="0.3"
                    max="10"
                    step="0.1"
                    value={textBoxHeight}
                    onChange={(e) => setTextBoxHeight(e.target.value)}
                    className="tactical-input w-16"
                    data-testid="text-height-input"
                  />
                </div>
              )}

              <Button 
                variant="outline" 
                size="sm"
                onClick={addElement}
                className="bg-green-600/20 hover:bg-green-600/30 border-green-600/50"
                data-testid="add-element-button"
              >
                <Square className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            {/* Preset label library — always visible; one-tap adds a text element */}
            <div
              className="flex flex-wrap items-center gap-2 px-1"
              data-testid="preset-labels-row"
            >
              <Label className="label-tactical whitespace-nowrap text-muted-foreground">
                Quick labels:
              </Label>
              {PRESET_LABELS.map((label) => (
                <Button
                  key={label}
                  variant="outline"
                  size="sm"
                  onClick={() => addLabelPreset(label)}
                  className="h-7 px-2.5 text-xs bg-zinc-800/40 hover:bg-cyan-600/20 border-zinc-600/50 hover:border-cyan-500/50"
                  data-testid={`preset-label-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  + {label}
                </Button>
              ))}
            </div>

            {/* Row 3: Group Placement */}
            <div className="flex flex-wrap items-center gap-3 p-2 bg-zinc-900/50 rounded-md border border-border/30">
              <div className="flex items-center gap-2">
                <LayoutGrid className="h-4 w-4 text-yellow-400" />
                <Label className="label-tactical whitespace-nowrap">Group</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Rows</Label>
                <Input
                  type="number"
                  min="1"
                  value={arrayRows}
                  onChange={(e) => setArrayRows(e.target.value)}
                  className="tactical-input w-14"
                  data-testid="array-rows-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Cols</Label>
                <Input
                  type="number"
                  min="1"
                  value={arrayCols}
                  onChange={(e) => setArrayCols(e.target.value)}
                  className="tactical-input w-14"
                  data-testid="array-cols-input"
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {selectedType === "chair" ? "(1.1m × 0.9m)" : ""}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={placeGroup}
                className="bg-cyan-600/20 hover:bg-cyan-600/30 border-cyan-600/50"
                data-testid="place-group-button"
              >
                <LayoutGrid className="h-4 w-4 mr-1" />
                Add Group
              </Button>
            </div>

            {/* Row 3c: Edit selected text element (in-place after placement) */}
            {selectedElement !== null && elements[selectedElement]?.type === "text" && (
              <div
                className="flex flex-wrap items-center gap-2 p-2 bg-zinc-800/40 rounded-md border border-zinc-500/30"
                data-testid="edit-text-panel"
              >
                <Label className="label-tactical whitespace-nowrap text-zinc-100">
                  Edit Text
                </Label>
                <Input
                  type="text"
                  value={elements[selectedElement].text || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setElements(prev => prev.map((el, i) => (i === selectedElement ? { ...el, text: value } : el)));
                  }}
                  className="tactical-input w-44"
                  placeholder="Label text"
                  data-testid="edit-text-content-input"
                />
                <Label className="text-xs text-muted-foreground">Font</Label>
                <Input
                  type="number"
                  min="8"
                  max="72"
                  value={elements[selectedElement].fontSize || 18}
                  onChange={(e) => {
                    const v = parseInt(e.target.value) || 18;
                    setElements(prev => prev.map((el, i) => (i === selectedElement ? { ...el, fontSize: v } : el)));
                  }}
                  className="tactical-input w-16"
                  data-testid="edit-text-fontsize-input"
                />
                <Label className="text-xs text-muted-foreground">W (m)</Label>
                <Input
                  type="number"
                  min="0.5"
                  max="20"
                  step="0.1"
                  value={((elements[selectedElement].width || GRID_SIZE * 3) / GRID_SIZE).toFixed(1)}
                  onChange={(e) => {
                    const v = Math.max(0.5, parseFloat(e.target.value) || 3);
                    setElements(prev => prev.map((el, i) => (i === selectedElement ? { ...el, width: v * GRID_SIZE } : el)));
                  }}
                  className="tactical-input w-16"
                  data-testid="edit-text-width-input"
                />
                <Label className="text-xs text-muted-foreground">H (m)</Label>
                <Input
                  type="number"
                  min="0.3"
                  max="10"
                  step="0.1"
                  value={((elements[selectedElement].height || GRID_SIZE * 0.8) / GRID_SIZE).toFixed(1)}
                  onChange={(e) => {
                    const v = Math.max(0.3, parseFloat(e.target.value) || 0.8);
                    setElements(prev => prev.map((el, i) => (i === selectedElement ? { ...el, height: v * GRID_SIZE } : el)));
                  }}
                  className="tactical-input w-16"
                  data-testid="edit-text-height-input"
                />
                <span className="text-xs text-muted-foreground">
                  Use <span className="text-foreground">Rotate CW/CCW</span> buttons →
                </span>
              </div>
            )}

            {/* Row 3b: Edit selected group */}
            {selectedGroup && groupMeta[selectedGroup] && (
              <div
                className="flex flex-wrap items-center gap-3 p-2 bg-cyan-950/40 rounded-md border border-cyan-500/30"
                data-testid="edit-group-panel"
              >
                <div className="flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-cyan-300" />
                  <Label className="label-tactical whitespace-nowrap text-cyan-200">
                    Edit Group #{selectedGroup}
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    ({groupMeta[selectedGroup].type})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Rows</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    defaultValue={groupMeta[selectedGroup].rows}
                    key={`gr-rows-${selectedGroup}-${groupMeta[selectedGroup].rows}`}
                    onChange={(e) => {
                      const v = parseInt(e.target.value) || 1;
                      setGroupMeta((prev) => ({
                        ...prev,
                        [selectedGroup]: { ...prev[selectedGroup], _pendingRows: v },
                      }));
                    }}
                    className="tactical-input w-16"
                    data-testid="edit-group-rows-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Cols</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    defaultValue={groupMeta[selectedGroup].cols}
                    key={`gr-cols-${selectedGroup}-${groupMeta[selectedGroup].cols}`}
                    onChange={(e) => {
                      const v = parseInt(e.target.value) || 1;
                      setGroupMeta((prev) => ({
                        ...prev,
                        [selectedGroup]: { ...prev[selectedGroup], _pendingCols: v },
                      }));
                    }}
                    className="tactical-input w-16"
                    data-testid="edit-group-cols-input"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const meta = groupMeta[selectedGroup] || {};
                    const nextRows = meta._pendingRows ?? meta.rows;
                    const nextCols = meta._pendingCols ?? meta.cols;
                    regenerateGroup(selectedGroup, nextRows, nextCols);
                  }}
                  className="bg-cyan-600/30 hover:bg-cyan-600/50 border-cyan-400/60"
                  data-testid="apply-group-resize-button"
                >
                  Apply
                </Button>
              </div>
            )}

            {/* Row 3: Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 mr-2 pr-2 border-r border-border/50">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={zoomOut}
                  disabled={zoom <= MIN_ZOOM}
                  data-testid="zoom-out-button"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={resetZoom}
                  className="w-16 font-mono text-xs"
                  data-testid="zoom-reset-button"
                >
                  {Math.round(zoom * 100)}%
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={zoomIn}
                  disabled={zoom >= MAX_ZOOM}
                  data-testid="zoom-in-button"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowGrid(!showGrid)}
                data-testid="toggle-grid-button"
              >
                <Grid3X3 className="h-4 w-4 mr-1" />
                Grid
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => rotateSelected(-1)}
                disabled={selectedElement === null}
                data-testid="rotate-ccw-button"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                -15°
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => rotateSelected(1)}
                disabled={selectedElement === null}
                data-testid="rotate-cw-button"
              >
                <RotateCw className="h-4 w-4 mr-1" />
                +15°
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={deleteSelected}
                disabled={selectedElement === null}
                data-testid="delete-element-button"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={clearAll}
                data-testid="clear-all-button"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Clear
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={downloadPlan}
                className="bg-yellow-600 hover:bg-yellow-700 border-yellow-600"
                data-testid="download-plan-button"
              >
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
              
              {/* Save/Load buttons */}
              <div className="flex items-center gap-1 ml-2 pl-2 border-l border-border/50">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={openLoadDialog}
                  className="bg-purple-600/20 hover:bg-purple-600/30 border-purple-600/50"
                  data-testid="load-plan-button"
                >
                  <FolderOpen className="h-4 w-4 mr-1" />
                  Load
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={openSaveDialog}
                  className="bg-green-600/20 hover:bg-green-600/30 border-green-600/50"
                  data-testid="save-plan-button"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </Button>
              </div>
            </div>
            
            {/* Current plan indicator */}
            {currentPlanName && (
              <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Current: <span className="text-cyan-400 font-medium">{currentPlanName}</span></span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Canvas */}
        <Card className="tactical-card">
          <CardContent className="p-2 sm:p-3">
            <div className="flex justify-center overflow-auto">
              <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                className="border border-border rounded-sm cursor-crosshair floor-plan-canvas touch-none"
                onClick={handleCanvasClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                data-testid="floor-plan-canvas"
              />
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="text-lg uppercase">Element Legend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {elementTypes.map((type) => (
                <div 
                  key={type.value}
                  className={`p-2 rounded-sm border transition-colors cursor-pointer ${
                    selectedType === type.value 
                      ? 'border-yellow-500/50 bg-yellow-500/10' 
                      : 'border-border/50 hover:border-border'
                  }`}
                  onClick={() => setSelectedType(type.value)}
                >
                  <div className="flex items-center gap-2">
                    {type.shape === "circle" ? (
                      <Circle className="h-4 w-4" style={{ color: type.color }} />
                    ) : (
                      <Square className="h-4 w-4" style={{ color: type.color }} />
                    )}
                    <span className="text-xs">{type.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <Card className="tactical-card">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Move className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {elements.length} elements placed
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-muted-foreground">
                  Round Tables: {elements.filter(e => e.type === 'table-round').length}
                </span>
                <span className="text-muted-foreground">
                  Cabaret: {elements.filter(e => e.type === 'table-round' && e.chairs < 10).length}
                </span>
                <span className="text-muted-foreground">
                  Banquet: {elements.filter(e => e.type === 'table-round' && e.chairs >= 10).length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Floor Plan</DialogTitle>
            <DialogDescription>
              {currentPlanId 
                ? "Update the existing plan or save as a new plan with a different name."
                : "Give your floor plan a name to save it for later."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Plan Name</Label>
              <Input
                value={savePlanName}
                onChange={(e) => setSavePlanName(e.target.value)}
                placeholder="e.g., Wedding Reception Layout"
                data-testid="save-plan-name-input"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <p>This plan contains:</p>
              <ul className="list-disc list-inside mt-1">
                <li>{elements.length} elements</li>
                <li>Room size: {roomWidth}m × {roomLength}m</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving || !savePlanName.trim()}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-plan-confirm-button"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Plan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Dialog */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Load Floor Plan</DialogTitle>
            <DialogDescription>
              Select a saved floor plan to load onto the canvas.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-80 overflow-y-auto">
            {savedPlans.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No saved plans yet</p>
                <p className="text-sm mt-1">Create your first floor plan and save it!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {savedPlans.map((plan) => (
                  <div 
                    key={plan.id}
                    className={`p-3 rounded-lg border transition-colors ${
                      currentPlanId === plan.id 
                        ? 'border-cyan-500 bg-cyan-500/10' 
                        : 'border-border hover:border-border/80 hover:bg-secondary/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 cursor-pointer" onClick={() => handleLoad(plan)}>
                        <p className="font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4 text-cyan-400" />
                          {plan.name}
                          {currentPlanId === plan.id && (
                            <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">Current</span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {plan.elements?.length || 0} elements • {plan.room_width}m × {plan.room_length}m
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Updated: {new Date(plan.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLoad(plan)}
                          className="text-cyan-400 hover:text-cyan-300"
                          data-testid={`load-plan-${plan.id}`}
                        >
                          <FolderOpen className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(plan.id, plan.name)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-plan-${plan.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoadDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FloorPlan;
